import { C } from "@bd/content";
import { artRng, Bitmap, hex, mix, OUTLINE, shade, type RGB } from "./bitmap";
import { furnitureSprite } from "./furniture";
import { leafy } from "./plants";
import { IsoPainter, spritePainter, T, type IsoSprite } from "./iso";
import { drawText } from "./pixelfont";

export interface LayoutRoom {
  id: string;
  type: string;
  x: number;
  y: number;
  w: number;
  h: number;
  door?: { x: number; y: number };
  furniture: { id: string; kind: string; x: number; y: number; rot: number }[];
}

/** One building on the lot (the main studio or an annex), in lot tiles. */
export interface LayoutPlot {
  x: number;
  y: number;
  w: number;
  h: number;
  /** "garage" | "office" | "studio" | "hq" for the main building, or the annex kind */
  kind: string;
  name: string;
  main: boolean;
}

export interface StudioLayout {
  tilesW: number;
  tilesH: number;
  level: string;
  rooms: LayoutRoom[];
  season?: "spring" | "summer" | "autumn" | "winter";
  /** the in-game year: furniture takes its era's look (typewriters in 1965, flat screens in 2030) */
  year?: number;
  /** every building on the lot; when left out, just the main building at tilesW × tilesH */
  plots?: LayoutPlot[];
  /** buildings next door you can buy: drawn closed, with a FOR SALE banner and the price */
  forSale?: (LayoutPlot & { id: string; price: string })[];
}

export interface SceneSprite {
  id: string;
  kind: string;
  img: Bitmap;
  em?: Bitmap;
  /** top-left pixel in the world bitmap */
  px: number;
  py: number;
  /** depth key: larger draws later (in front) */
  depth: number;
  roomId?: string;
}

export interface SceneLight {
  /** world pixel position of the light */
  px: number;
  py: number;
  color: RGB | [number, number, number];
  radius: number; // world pixels
  strength: number;
}

export interface Seat {
  roomId: string;
  furnitureId: string;
  kind: string;
  /** unit coordinates of where a person sits/stands */
  ux: number;
  uy: number;
  view: "front" | "back";
}

export interface StudioScene {
  w: number;
  h: number;
  ox: number;
  oy: number;
  base: Bitmap;
  baseEm: Bitmap;
  sprites: SceneSprite[];
  lights: SceneLight[];
  windows: { ux: number; uy: number; plane: "x" | "y"; width: number }[];
  seats: Seat[];
  /** project unit coords to world pixels */
  P: (x: number, y: number, z?: number) => [number, number];
  /** the main building in world pixels (walls included), for framing the camera */
  bounds: { x: number; y: number; w: number; h: number };
  /** every building on the lot together */
  campus: { x: number; y: number; w: number; h: number };
  /** road lanes for passing cars, in unit coordinates */
  lanes: { uy: number; x0: number; x1: number; dir: 1 | -1 }[];
  /** pavements for people walking by, in unit coordinates */
  walks: { uy: number; x0: number; x1: number }[];
  /** buildings for sale, in tiles, for clicking */
  forSale: { id: string; kind: string; x: number; y: number; w: number; h: number }[];
  /** where a delivery van pulls up: the kerb in front of the main door, in unit coordinates */
  kerb: { ux: number; uy: number };
}

export const WALL_H = 30;
const LOW_WALL = 10;
const LAWN = 5; // tiles of lawn around the buildings
const MARGIN = 19; // lawn, then the town: neighbours behind and beside, the street and a park out front

const GRASS: RGB[] = [[78, 132, 74], [72, 124, 70]];
const GRASS_SEASON: Record<string, RGB[]> = {
  spring: [[86, 142, 78], [80, 134, 74]],
  summer: [[78, 132, 74], [72, 124, 70]],
  autumn: [[130, 120, 70], [120, 112, 66]],
  winter: [[214, 222, 230], [200, 210, 220]],
};

export function sceneSize(tilesW: number, tilesH: number): { w: number; h: number; ox: number; oy: number } {
  const M = MARGIN * T;
  const ox = (tilesH * T + 2 * M) * 2 + 2;
  const oy = Math.max(2 * M, WALL_H) + 4;
  const w = ox + (tilesW * T + 2 * M) * 2 + 2;
  const h = oy + (tilesW + tilesH) * T + 2 * M + 4;
  return { w, h, ox, oy };
}

function sky(w: number, h: number): Bitmap {
  const im = new Bitmap(w, h);
  for (let y = 0; y < h; y++) {
    const c = mix([112, 160, 214], [236, 196, 150], y / Math.max(1, h - 1));
    for (let x = 0; x < w; x++) im.set(x, y, c);
  }
  im.strokeBox(0, 0, w - 1, h - 1, [236, 228, 210]);
  im.line(w >> 1, 0, w >> 1, h, [236, 228, 210]);
  im.line(0, h >> 1, w, h >> 1, [236, 228, 210]);
  return im;
}

/** Corridor floors grow up with the company: bare concrete, office carpet, studio boards, polished stone. */
const CORRIDOR: Record<string, RGB> = { garage: [150, 146, 138], office: [178, 160, 136], studio: [176, 136, 98], hq: [206, 200, 188] };

function floorColor(type: string, tx: number, ty: number, r: () => number, level = "garage"): RGB {
  const base = type ? hex(C.room[type]?.palette.floor ?? "#868A92") : (CORRIDOR[level] ?? CORRIDOR.garage);
  const c = (tx + ty) % 2 ? shade(base, 0.94) : base;
  const j = Math.floor(r() * 9) - 4;
  return [Math.max(0, Math.min(255, c[0] + j)), Math.max(0, Math.min(255, c[1] + j)), Math.max(0, Math.min(255, c[2] + j))];
}

/** Corridor floors for annexes: office carpet, a concrete hall, a shop's tiles. */
const ANNEX_FLOOR: Record<string, string> = { annex: "office", hall: "garage", corner: "hq" };

/** Builds the whole lot: the town, the lawn, every building's floor, walls and decor, as a base bitmap plus depth-sorted sprites. */
export function buildStudio(layout: StudioLayout, seed = 1): StudioScene {
  const plotsIn: LayoutPlot[] = layout.plots?.length ? layout.plots : [{ x: 0, y: 0, w: layout.tilesW, h: layout.tilesH, kind: layout.level, name: "", main: true }];
  const sale = layout.forSale ?? [];
  const LW = Math.max(...plotsIn.map((q) => q.x + q.w), ...sale.map((q) => q.x + q.w));
  const LH = Math.max(...plotsIn.map((q) => q.y + q.h), ...sale.map((q) => q.y + q.h));
  const { w, h, ox, oy } = sceneSize(LW, LH);
  const base = new IsoPainter(w, h, ox, oy);
  const r = artRng(seed);
  const sprites: SceneSprite[] = [];
  const lights: SceneLight[] = [];
  const seats: Seat[] = [];
  const windows: StudioScene["windows"] = [];
  const season = layout.season ?? "summer";
  const grass = GRASS_SEASON[season] ?? GRASS;
  const winter = season === "winter";
  const mainPlot = plotsIn.find((q) => q.main) ?? plotsIn[0];
  const inPlot = (tx: number, ty: number, pad = 0) => plotsIn.find((q) => tx >= q.x - pad && tx < q.x + q.w + pad && ty >= q.y - pad && ty < q.y + q.h + pad);
  const inSale = (tx: number, ty: number, pad = 0) => sale.some((q) => tx >= q.x - pad && tx < q.x + q.w + pad && ty >= q.y - pad && ty < q.y + q.h + pad);
  // each building's front door (two tiles wide) and its path down to the pavement
  const doorX = (q: LayoutPlot) => q.x + 2;
  const onPath = (tx: number, ty: number) => [...plotsIn, ...sale].some((q) => ty >= q.y + q.h && (tx === doorX(q) || tx === doorX(q) + 1));

  // ---------------------------------------------------------------- ground
  const PAVE = LH + 3; // pavement in front of the lot
  const ROAD = LH + 4; // three road rows
  const FAR = ROAD + 3; // the far pavement
  const PAVING: RGB[] = [[188, 184, 176], [178, 174, 166]];
  for (let tx = -MARGIN; tx < LW + MARGIN; tx++) for (let ty = -MARGIN; ty < LH + MARGIN; ty++) {
    if (inPlot(tx, ty)) continue;
    const g = Math.floor(r() * 13) - 6;
    let c: RGB = grass[(tx + ty) & 1].map((v) => v + g) as unknown as RGB;
    const town = tx < -LAWN || tx >= LW + LAWN || ty < -LAWN;
    if (town) c = PAVING[(tx + ty) & 1].map((v) => v - 18 + (g >> 1)) as unknown as RGB;
    if (ty >= LH && ty < PAVE && onPath(tx, ty)) c = [196, 190, 180];
    if (ty === PAVE || ty === FAR) c = PAVING[tx & 1];
    if (ty >= ROAD && ty < ROAD + 3) c = [64 + (g >> 1), 66 + (g >> 1), 74 + (g >> 1)];
    base.floor(tx * T, ty * T, T, T, c, 0);
  }
  // kerbs and lane markings
  base.line([-MARGIN * T, ROAD * T, 0.03], [(LW + MARGIN) * T, ROAD * T, 0.03], [120, 116, 110]);
  base.line([-MARGIN * T, (ROAD + 3) * T, 0.03], [(LW + MARGIN) * T, (ROAD + 3) * T, 0.03], [120, 116, 110]);
  for (let u = -MARGIN * T; u < (LW + MARGIN) * T; u += 8) base.line([u, (ROAD + 1.5) * T, 0.04], [u + 4, (ROAD + 1.5) * T, 0.04], [236, 214, 120]);
  const walks: StudioScene["walks"] = [
    { uy: (PAVE + 0.5) * T, x0: -MARGIN * T, x1: (LW + MARGIN) * T },
    { uy: (FAR + 0.5) * T, x0: -MARGIN * T, x1: (LW + MARGIN) * T },
  ];
  const lanes: StudioScene["lanes"] = [
    { uy: (ROAD + 0.75) * T, x0: -MARGIN * T, x1: (LW + MARGIN) * T, dir: -1 },
    { uy: (ROAD + 2.25) * T, x0: -MARGIN * T, x1: (LW + MARGIN) * T, dir: 1 },
  ];

  const place = (id: string, kind: string, spr: IsoSprite, tx: number, ty: number) => {
    const ux = tx * T;
    const uy = ty * T;
    const [px, py] = base.P(ux, uy, 0);
    sprites.push({ id, kind, img: spr.img, em: spr.em, px: Math.round(px - spr.ax), py: Math.round(py - spr.ay), depth: ux + (spr.w * T) / 2 + uy + (spr.h * T) / 2 });
  };
  const outdoor = (kind: string, tx: number, ty: number, seedN: number) => {
    let spr: IsoSprite;
    try {
      spr = kind === "tree" ? outdoorTree(seedN, season) : kind === "bush" ? outdoorBush(seedN, season) : kind === "lamp" ? streetLamp() : furnitureSprite(kind, 0, seedN, layout.year);
    } catch {
      return;
    }
    place(`out:${kind}:${tx}:${ty}`, kind, spr, tx, ty);
  };

  // ---------------------------------------------------------------- the town
  // neighbours behind the lot, then down both sides; a row of shops and a park across the road
  const row = (x0: number, x1: number, y0: number, depth: number, minH: number, maxH: number, along: "x" | "y", key: string) => {
    let u = along === "x" ? x0 : y0;
    const end = along === "x" ? x1 : x0;
    let n = 0;
    while (u < end - 3) {
      const len = Math.min(end - u, 5 + Math.floor(r() * 5));
      const ht = minH + Math.floor(r() * (maxH - minH));
      const spr = neighbourBuilding(along === "x" ? len : depth, along === "x" ? depth : len, ht, Math.floor(r() * 1000), winter);
      if (along === "x") place(`town:${key}:${n}`, "town", spr, u, y0);
      else place(`town:${key}:${n}`, "town", spr, x0 - depth, u);
      // lit windows glow a little into the street at night
      u += len + 1;
      n++;
    }
  };
  const BACK = -LAWN - 1;
  row(-MARGIN + 1, LW + MARGIN - 1, BACK - 7, 7, 26, 56, "x", "back");
  // left side: a column of buildings standing along x = -LAWN - 1
  for (let ty = -LAWN, n = 0; ty < PAVE - 2; n++) {
    const len = Math.min(PAVE - 2 - ty, 5 + Math.floor(r() * 4));
    if (len < 3) break;
    const spr = neighbourBuilding(7, len, 22 + Math.floor(r() * 26), Math.floor(r() * 1000), winter);
    place(`town:left:${n}`, "town", spr, -LAWN - 8, ty);
    ty += len + 1;
  }
  // right side: lower, so they never hide your own buildings
  for (let ty = -LAWN, n = 0; ty < PAVE - 2; n++) {
    const len = Math.min(PAVE - 2 - ty, 5 + Math.floor(r() * 4));
    if (len < 3) break;
    const spr = neighbourBuilding(6, len, 16 + Math.floor(r() * 16), Math.floor(r() * 1000), winter);
    place(`town:right:${n}`, "town", spr, LW + LAWN + 1, ty);
    ty += len + 1;
  }
  // across the road: shops with awnings, then a strip of park
  for (let tx = -MARGIN + 2, n = 0; tx < LW + MARGIN - 6; n++) {
    const len = 5 + Math.floor(r() * 4);
    if (r() < 0.3) {
      for (let k = 0; k < len; k += 2) outdoor("tree", tx + k, FAR + 2 + (k % 3), Math.floor(r() * 1000));
    } else {
      const spr = neighbourBuilding(len, 4, 11 + Math.floor(r() * 4), Math.floor(r() * 1000), winter, true);
      place(`town:front:${n}`, "town", spr, tx, FAR + 1);
    }
    tx += len + 1;
  }
  // street lamps along both pavements
  for (let tx = -MARGIN + 3; tx < LW + MARGIN - 2; tx += 7) {
    outdoor("lamp", tx, PAVE, 1);
    const [lx, ly] = base.P(tx * T + 3, PAVE * T + 3, 18);
    lights.push({ px: lx, py: ly + 12, color: [1, 0.86, 0.6], radius: 4 * T, strength: 0.22 });
  }

  // ---------------------------------------------------------------- the lawn
  const lawn = (tx: number, ty: number) => tx >= -LAWN && tx < LW + LAWN && ty >= -LAWN && ty < PAVE - 1 && !inPlot(tx, ty, 1) && !inSale(tx, ty, 1) && !onPath(tx, ty);
  // buildings next door that are for sale: closed, with a banner, a board on the lawn and their price
  sale.forEach((q, i) => {
    const spr = neighbourBuilding(q.w, q.h, 30, 401 + i * 37, winter, false, ["FOR SALE", q.price]);
    place(`sale:${q.id}`, "sale", spr, q.x, q.y);
    const board = saleBoard();
    place(`saleboard:${q.id}`, "saleboard", board, doorX(q) + 3, q.y + q.h + 1);
    // a billboard on the roof, facing you: what it is and what it costs
    const bb = rooftopBillboard(q.name, q.price);
    const [cx, cy] = base.P((q.x + q.w / 2) * T, (q.y + q.h / 2) * T, 30);
    sprites.push({ id: `salebill:${q.id}`, kind: "salebill", img: bb, px: Math.round(cx - bb.w / 2), py: Math.round(cy - bb.h + 4), depth: (q.x + q.w) * T + (q.y + q.h) * T + 2 });
  });
  for (let tx = -LAWN; tx < LW + LAWN; tx += 3) for (let ty = -LAWN; ty < PAVE - 1; ty += 3) {
    const jx = tx + Math.floor(r() * 2);
    const jy = ty + Math.floor(r() * 2);
    if (!lawn(jx, jy)) continue;
    const roll = r();
    if (roll < 0.42) outdoor("tree", jx, jy, Math.floor(r() * 1000));
    else if (roll < 0.66) outdoor("bush", jx, jy, Math.floor(r() * 1000));
  }
  for (let tx = -LAWN + 1; tx < LW + LAWN - 1; tx += 5) if (lawn(tx, PAVE - 1)) outdoor("tree", tx, PAVE - 1, tx + 7);
  if (lawn(doorX(mainPlot) + 3, PAVE - 1)) outdoor("bench", doorX(mainPlot) + 3, PAVE - 1, 3);

  // ---------------------------------------------------------------- floors
  const roomAt = (tx: number, ty: number) => layout.rooms.find((rm) => tx >= rm.x && tx < rm.x + rm.w && ty >= rm.y && ty < rm.y + rm.h);
  for (const q of plotsIn) {
    const style = q.main ? layout.level : ANNEX_FLOOR[q.kind] ?? "office";
    for (let tx = q.x; tx < q.x + q.w; tx++) for (let ty = q.y; ty < q.y + q.h; ty++) {
      const rm = roomAt(tx, ty);
      const c = floorColor(rm?.type ?? "", tx, ty, r, style);
      base.floor(tx * T, ty * T, T, T, c, 0);
      if (!rm && style === "studio") base.line([tx * T, ty * T + 3, 0.01], [tx * T + T, ty * T + 3, 0.01], shade(c, 0.86));
      if (!rm && style === "hq" && (tx + ty) % 2 === 0) base.floor(tx * T + 1, ty * T + 1, T - 2, T - 2, shade(c, 0.95), 0.01);
      if (rm && (rm.type === "playtest_lab" || rm.type === "art_studio" || rm.type === "break_room")) base.line([tx * T, ty * T + 3, 0.01], [tx * T + T, ty * T + 3, 0.01], shade(c, 0.88));
    }
  }

  // ---------------------------------------------------------------- walls
  const wallTop: RGB = [120, 74, 50];
  const wallSeg = (x: number, y: number, horizontal: boolean, key: string, roomId?: string, height = LOW_WALL, col: RGB = [232, 222, 200], cap: RGB = [140, 92, 60]) => {
    const p = new IsoPainter(36, 40 + height, 18, 28 + height - LOW_WALL);
    // one smooth panel per segment (no outline, a hair of overlap) so a run reads as a solid wall, with a skirting line
    if (horizontal) {
      p.box(0, -0.6, 0, 3.1, 1.2, height, col, { top: cap, outline: false });
      p.line([0, 0.6, 1.5], [3.1, 0.6, 1.5], shade(col, 0.72));
    } else {
      p.box(-0.6, 0, 0, 1.2, 3.1, height, col, { top: cap, outline: false });
      p.line([0.6, 0, 1.5], [0.6, 3.1, 1.5], shade(col, 0.6));
    }
    const [px, py] = base.P(x, y, 0);
    sprites.push({ id: key, kind: "wall", img: p.img, px: Math.round(px - 18), py: Math.round(py - 28 - height + LOW_WALL), depth: x + y + 1.5, roomId });
  };
  for (const q of plotsIn) {
    const brick = q.main && layout.level === "garage";
    const X0 = q.x * T;
    const Y0 = q.y * T;
    const QW = q.w * T;
    const QH = q.h * T;
    // full-height back wall, and the left wall on the main building only (the cutaway shows the rest)
    base.box(X0, Y0 - 2, 0, QW, 2, WALL_H, brick ? [176, 92, 72] : q.main ? [226, 214, 190] : [214, 206, 192], { top: wallTop });
    if (q.main) base.box(X0 - 2, Y0 - 2, 0, 2, QH + 2, WALL_H, brick ? [156, 82, 64] : [206, 194, 172], { top: wallTop });
    else for (let u = Y0; u < Y0 + QH; u += 3) wallSeg(X0, u, false, `bw:${q.kind}:l:${u}`, undefined, LOW_WALL, [214, 206, 192]);
    // the building's own front and right walls, low, with its front door
    for (let u = X0; u < X0 + QW; u += 3) {
      const tx = Math.floor(u / T);
      if (tx === doorX(q) || tx === doorX(q) + 1) continue;
      wallSeg(u, Y0 + QH, true, `bw:${q.kind}:f:${u}`, undefined, LOW_WALL, brick ? [176, 92, 72] : [214, 206, 192]);
    }
    for (let u = Y0; u < Y0 + QH; u += 3) wallSeg(X0 + QW, u, false, `bw:${q.kind}:r:${u}`, undefined, LOW_WALL, brick ? [156, 82, 64] : [214, 206, 192]);
    // rooms against the back wall get their own wallpaper (the garage stays bare brick)
    for (const rm of brick ? [] : layout.rooms) {
      if (rm.x < q.x || rm.x + rm.w > q.x + q.w || rm.y !== q.y) continue;
      const pal = hex(C.room[rm.type]?.palette.wall ?? "#E2D6BE");
      base.quad([[rm.x * T, Y0, 4], [(rm.x + rm.w) * T, Y0, 4], [(rm.x + rm.w) * T, Y0, WALL_H - 1], [rm.x * T, Y0, WALL_H - 1]], pal);
      base.quad([[rm.x * T, Y0, 0], [(rm.x + rm.w) * T, Y0, 0], [(rm.x + rm.w) * T, Y0, 4], [rm.x * T, Y0, 4]], shade(pal, 0.6));
    }
    if (q.main && !brick) for (const rm of layout.rooms) {
      if (rm.x !== 0) continue;
      const pal = hex(C.room[rm.type]?.palette.wall ?? "#E2D6BE");
      base.quad([[0, rm.y * T, 4], [0, (rm.y + rm.h) * T, 4], [0, (rm.y + rm.h) * T, WALL_H - 1], [0, rm.y * T, WALL_H - 1]], shade(pal, 0.92));
      base.quad([[0, rm.y * T, 0], [0, (rm.y + rm.h) * T, 0], [0, (rm.y + rm.h) * T, 4], [0, rm.y * T, 4]], shade(pal, 0.55));
    }
    if (brick) for (let k = X0; k < X0 + QW; k += 3) for (let z = 3; z < WALL_H - 2; z += 3) base.line([k + ((z / 3) % 2) * 1.5, Y0 - 0.01, z], [k + 1 + ((z / 3) % 2) * 1.5, Y0 - 0.01, z], [150, 76, 60]);
    // windows along the back wall (and the main building's left wall)
    for (let tx = q.x + 1; tx < q.x + q.w - 1; tx += 4) {
      base.decal(sky(16, 12), "y", tx * T + 1, Y0, 24, true);
      windows.push({ ux: tx * T + 1, uy: Y0, plane: "y", width: 8 });
    }
    if (q.main) for (let ty = 2; ty < q.h - 1; ty += 5) {
      base.decal(sky(14, 11), "x", 0, ty * T + 2, 23, true);
      windows.push({ ux: 0, uy: ty * T + 2, plane: "x", width: 7 });
    }
    // the sign: the company on the main building, each annex by name
    if (!brick) {
      const text = q.main ? "B&DICE" : q.name.toUpperCase().slice(0, 14);
      const sw = Math.max(36, text.length * 5 + 6);
      const sign = new Bitmap(sw, 9);
      sign.rect(0, 0, sw, 9, [40, 46, 86]);
      sign.strokeBox(0, 0, sw - 1, 8, [210, 170, 80]);
      drawText(sign, 3, 1, text, [255, 205, 117]);
      base.decal(sign, "y", Math.max(q.x + 2, q.x + q.w - Math.ceil(sw / 4) - 2) * T, Y0, 28);
    }
    // ceiling lights down the corridors
    if (!(q.main && layout.level === "garage")) for (let tx = q.x + 2; tx < q.x + q.w; tx += 6) for (let ty = q.y + 2; ty < q.y + q.h; ty += 6) {
      if (roomAt(tx, ty)) continue;
      const [lx, ly] = base.P(tx * T, ty * T, WALL_H - 6);
      lights.push({ px: lx, py: ly + 10, color: [0.92, 0.9, 0.86], radius: 5 * T, strength: 0.22 });
    }
  }

  // ---------------------------------------------------------------- the garage's own clutter, on floor no room uses
  if (layout.level === "garage") {
    // props never overlap a room or each other
    const taken = new Set<string>();
    const take = (tx: number, ty: number, w: number, h: number) => {
      for (let j = ty; j < ty + h; j++) for (let i = tx; i < tx + w; i++) taken.add(`${i},${j}`);
    };
    const free = (tx: number, ty: number, w: number, h: number) => {
      for (let j = ty; j < ty + h; j++) for (let i = tx; i < tx + w; i++) if (i < mainPlot.x || j < mainPlot.y || i >= mainPlot.x + mainPlot.w || j >= mainPlot.y + mainPlot.h || roomAt(i, j) || taken.has(`${i},${j}`)) return false;
      return true;
    };
    // the family car, nose to the back wall, until you move out
    for (let tx = mainPlot.x + mainPlot.w - 3; tx >= mainPlot.x; tx--) {
      const ty = mainPlot.y + mainPlot.h - 4;
      if (free(tx, ty, 2, 4)) {
        take(tx, ty, 2, 4);
        const car = familyCar();
        const [px, py] = base.P(tx * T, ty * T, 0);
        sprites.push({ id: "garage:car", kind: "car", img: car.img, px: Math.round(px - car.ax), py: Math.round(py - car.ay), depth: tx * T + T + ty * T + 2 * T });
        // an oil stain under the engine
        base.floor(tx * T + 2, ty * T + 1, T * 2 - 4, 3, [118, 114, 108], 0.01);
        // the roll-up door the car came in by: rolled up, so you can see in
        const fy = (mainPlot.y + mainPlot.h) * T;
        const x0 = tx * T - 1;
        const x1 = (tx + 2) * T + 1;
        for (let i = sprites.length - 1; i >= 0; i--) {
          const s = sprites[i];
          if (!s.id.startsWith("bw:") || !s.id.includes(":f:")) continue;
          const u = Number(s.id.split(":").pop());
          if (u >= x0 - 2 && u < x1) sprites.splice(i, 1);
        }
        const door = new IsoPainter(44, 52, 8, 32);
        const L = x1 - x0;
        const post: RGB = [150, 80, 62];
        door.box(0, -0.6, 0, 1.4, 1.2, 20, post, { top: [120, 64, 50] });
        door.box(L - 1.4, -0.6, 0, 1.4, 1.2, 20, post, { top: [120, 64, 50] });
        // the rolled door in its drum under the header, with the bottom slats showing
        door.box(0, -0.8, 14, L, 1.6, 3, [214, 210, 198], { top: [232, 228, 218] });
        for (let z = 14.5; z < 17; z += 1) door.line([1.4, 0.9, z], [L - 1.4, 0.9, z], [178, 172, 160]);
        door.box(0, -0.8, 17, L, 1.6, 3, [140, 92, 60], { top: [120, 74, 50] });
        // a handle at the bottom edge
        door.line([L / 2 - 1, 0.9, 14.2], [L / 2 + 1, 0.9, 14.2], [80, 80, 86]);
        const [dpx, dpy] = base.P(x0, fy, 0);
        sprites.push({ id: "garage:door", kind: "wall", img: door.img, px: Math.round(dpx - 8), py: Math.round(dpy - 32), depth: x0 + fy + 1.5 });
        break;
      }
    }
    // a pegboard of tools on the back wall, over a workbench
    const free0 = [...Array(mainPlot.w).keys()].map((i) => mainPlot.x + i).filter((tx) => free(tx, mainPlot.y, 1, 1));
    if (free0.length >= 2) {
      const tx = free0[free0.length - 2];
      take(tx, mainPlot.y, 2, 1);
      const peg = new Bitmap(24, 14);
      peg.rect(0, 0, 24, 14, [176, 140, 96]);
      for (let y = 2; y < 14; y += 3) for (let x = 2; x < 24; x += 3) peg.set(x, y, [120, 92, 60]);
      peg.rect(3, 3, 2, 8, [90, 96, 110]);
      peg.rect(8, 4, 6, 2, [196, 60, 50]);
      peg.rect(16, 3, 2, 7, [60, 120, 180]);
      peg.rect(19, 6, 3, 3, [220, 180, 60]);
      base.decal(peg, "y", tx * T, mainPlot.y * T, 22);
      try {
        const bench = furnitureSprite("packing_table", 0, 7, layout.year);
        const [px, py] = base.P(tx * T, mainPlot.y * T, 0);
        sprites.push({ id: "garage:bench", kind: "bench", img: bench.img, px: Math.round(px - bench.ax), py: Math.round(py - bench.ay), depth: tx * T + T + mainPlot.y * T + T / 2 });
      } catch {
        // no bench sprite: the pegboard is enough
      }
    }
    // shelves of prototypes and a stack of boxes along the left
    const clutter: [string, number, number][] = [["shelf_rack", 0, mainPlot.h - 2], ["box_stack", 2, mainPlot.h - 1], ["trolley", 3, mainPlot.h - 1], ["paper_stack", 0, mainPlot.h - 3], ["fridge", 1, 0]];
    // the family's kitchen table, where the first games get played: wherever it fits, with two stools
    kitchen: for (let ty = mainPlot.y + 1; ty < mainPlot.y + mainPlot.h - 2; ty++) for (let tx = mainPlot.x + 1; tx < mainPlot.x + mainPlot.w - 3; tx++) {
      if (!free(tx, ty, 3, 3)) continue;
      clutter.push(["game_table", tx - mainPlot.x, ty - mainPlot.y], ["stool", tx - mainPlot.x, ty - mainPlot.y + 2], ["stool", tx - mainPlot.x + 2, ty - mainPlot.y + 2]);
      break kitchen;
    }
    for (const [kind, dx, dy] of clutter) {
      const tx = mainPlot.x + dx;
      const ty = mainPlot.y + dy;
      try {
        const spr = furnitureSprite(kind, 0, dx * 7 + dy, layout.year);
        if (!free(tx, ty, spr.w, spr.h)) continue;
        take(tx, ty, spr.w, spr.h);
        const [px, py] = base.P(tx * T, ty * T, 0);
        sprites.push({ id: `garage:${kind}:${dx}:${dy}`, kind, img: spr.img, em: spr.em, px: Math.round(px - spr.ax), py: Math.round(py - spr.ay), depth: tx * T + (spr.w * T) / 2 + ty * T + (spr.h * T) / 2 });
      } catch {
        // skip a prop we can't draw
      }
    }
    // a bare bulb
    const [bx, by] = base.P((mainPlot.x + mainPlot.w / 2 + 2) * T, (mainPlot.y + mainPlot.h / 2 + 1) * T, WALL_H - 4);
    lights.push({ px: bx, py: by + 10, color: [1, 0.86, 0.6], radius: 6 * T, strength: 0.4 });
  }

  // ---------------------------------------------------------------- rooms
  for (const rm of layout.rooms) {
    const q = inPlot(rm.x, rm.y) ?? mainPlot;
    const door = rm.door ?? { x: rm.x + Math.floor(rm.w / 2), y: rm.y + rm.h };
    const cap = shade(hex(C.room[rm.type]?.palette.wall ?? "#8C5C3C"), 0.8);
    // room partitions stand a little taller than the building's cut-away front, so rooms read as rooms
    // each room's partitions take its own colour (softened), so rooms read apart at a glance
    const roomWall = mix(hex(C.room[rm.type]?.palette.wall ?? "#E2D6BE"), [232, 222, 200], 0.35);
    const seg = (x: number, y: number, horizontal: boolean, key: string) => wallSeg(x, y, horizontal, key, rm.id, LOW_WALL + 3, roomWall, cap);
    // door posts: two short pillars either side of the doorway
    const post = (ux: number, uy: number, key: string) => {
      const pp = new IsoPainter(16, 40, 8, 30);
      // a door frame the colour of the wall, no taller than it
      pp.box(-0.5, -0.5, 0, 1, 1, LOW_WALL + 3, shade(roomWall, 0.85), { top: cap, outline: false });
      const [px, py] = base.P(ux, uy, 0);
      sprites.push({ id: key, kind: "wall", img: pp.img, px: Math.round(px - 8), py: Math.round(py - 30), depth: ux + uy + 1.6, roomId: rm.id });
    };
    if (door.y === rm.y + rm.h && rm.y + rm.h < q.y + q.h) {
      post(door.x * T, (rm.y + rm.h) * T, `dp:${rm.id}:a`);
      post((door.x + 1) * T, (rm.y + rm.h) * T, `dp:${rm.id}:b`);
    } else if (door.x === rm.x + rm.w && rm.x + rm.w < q.x + q.w) {
      post((rm.x + rm.w) * T, door.y * T, `dp:${rm.id}:a`);
      post((rm.x + rm.w) * T, (door.y + 1) * T, `dp:${rm.id}:b`);
    }
    // front edge (y = y + h) and right edge (x = x + w); back edges are the walls or a neighbour's front edge
    for (let u = rm.x * T; u < (rm.x + rm.w) * T; u += 3) {
      const tx = Math.floor(u / T);
      if (door.y === rm.y + rm.h && tx === door.x) continue;
      if (rm.y + rm.h >= q.y + q.h) continue;
      seg(u, (rm.y + rm.h) * T, true, `w:${rm.id}:f:${u}`);
    }
    for (let u = rm.y * T; u < (rm.y + rm.h) * T; u += 3) {
      const ty = Math.floor(u / T);
      if (door.x === rm.x + rm.w && ty === door.y) continue;
      if (rm.x + rm.w >= q.x + q.w) continue;
      seg((rm.x + rm.w) * T, u, false, `w:${rm.id}:r:${u}`);
    }
    if (rm.y > q.y) for (let u = rm.x * T; u < (rm.x + rm.w) * T; u += 3) if (!layout.rooms.some((o) => o !== rm && o.y + o.h === rm.y && u / T >= o.x && u / T < o.x + o.w)) {
      const tx = Math.floor(u / T);
      if (door.y === rm.y && tx === door.x) continue;
      seg(u, rm.y * T, true, `w:${rm.id}:b:${u}`);
    }
    if (rm.x > q.x) for (let u = rm.y * T; u < (rm.y + rm.h) * T; u += 3) if (!layout.rooms.some((o) => o !== rm && o.x + o.w === rm.x && u / T >= o.y && u / T < o.y + o.h)) {
      const ty = Math.floor(u / T);
      if (door.x === rm.x && ty === door.y) continue;
      seg(rm.x * T, u, false, `w:${rm.id}:l:${u}`);
    }
    for (const f of rm.furniture) {
      let spr: IsoSprite;
      try {
        spr = furnitureSprite(f.kind, f.rot, hashId(f.id), layout.year);
      } catch {
        continue;
      }
      const ux = (rm.x + f.x) * T;
      const uy = (rm.y + f.y) * T;
      const [px, py] = base.P(ux, uy, 0);
      // rugs and other flat things lie under everything else
      const depth = C.furniture[f.kind]?.floor ? -1000 + ux + uy : ux + (spr.w * T) / 2 + uy + (spr.h * T) / 2;
      sprites.push({ id: f.id, kind: f.kind, img: spr.img, em: spr.em, px: Math.round(px - spr.ax), py: Math.round(py - spr.ay), depth, roomId: rm.id });
      for (const l of spr.lights ?? []) {
        const [lx, ly] = base.P(ux + l.x, uy + l.y, l.z);
        lights.push({ px: lx, py: ly, color: l.color, radius: l.radius * 2.4, strength: l.strength });
      }
      const k = C.furniture[f.kind];
      if (k?.seat) seats.push({ roomId: rm.id, furnitureId: f.id, kind: f.kind, ux: ux + (spr.w * T) / 2, uy: uy + spr.h * T + 1.5, view: "back" });
    }
    // a soft ceiling light per room; the strong warm pools come from the lamps actually in it
    const [lx, ly] = base.P((rm.x + rm.w / 2) * T, (rm.y + rm.h / 2) * T, WALL_H - 6);
    lights.push({ px: lx, py: ly + 10, color: [1, 0.86, 0.66], radius: Math.max(rm.w, rm.h) * T * 0.8, strength: 0.16 });
  }

  const box = (x0: number, y0: number, x1: number, y1: number) => {
    const [bx0] = base.P(x0 * T, y1 * T, 0);
    const [bx1] = base.P(x1 * T, y0 * T, 0);
    const [, by0] = base.P(x0 * T, y0 * T, WALL_H);
    const [, by1] = base.P(x1 * T, y1 * T, 0);
    return { x: bx0, y: by0, w: bx1 - bx0, h: by1 - by0 };
  };
  const bounds = box(mainPlot.x, mainPlot.y, mainPlot.x + mainPlot.w, mainPlot.y + mainPlot.h);
  const campus = box(0, 0, LW, LH);
  const forSale = sale.map((q) => ({ id: q.id, kind: q.kind, x: q.x, y: q.y, w: q.w, h: q.h }));
  const kerb = { ux: (doorX(mainPlot) - 1) * T, uy: (ROAD + 0.75) * T - 2.5 };
  return { w, h, ox, oy, base: base.img, baseEm: base.em, sprites: sprites.sort((a, b) => a.depth - b.depth), lights, windows, seats, P: (x, y, z = 0) => base.P(x, y, z), bounds, campus, lanes, walks, forSale, kerb };
}

/** A neighbour's building: walls, a flat roof, rows of windows (some lit), a door; low ones get a shop awning. */
function neighbourBuilding(wTiles: number, dTiles: number, height: number, seed: number, winter: boolean, shop = false, banner?: [string, string]): IsoSprite {
  const p = spritePainter(wTiles, dTiles, height + 6);
  const WALLS: RGB[] = [[196, 140, 112], [170, 176, 190], [214, 196, 160], [150, 120, 108], [188, 200, 186], [208, 170, 140], [128, 136, 160]];
  const wall = WALLS[seed % WALLS.length];
  const roof: RGB = winter ? [232, 236, 240] : shade(wall, 0.62);
  const W = wTiles * T;
  const D = dTiles * T;
  p.box(0, 0, 0, W, D, height, wall, { top: roof });
  p.box(0, 0, height, W, 0.8, 1.6, shade(wall, 0.8));
  p.box(0, D - 0.8, height, W, 0.8, 1.6, shade(wall, 0.8));
  // windows on the two faces you can see
  const pane = (lit: boolean) => {
    const im = new Bitmap(5, 6);
    im.rect(0, 0, 5, 6, [60, 52, 50]);
    im.rect(1, 1, 3, 4, lit ? [255, 214, 140] : [126, 160, 196]);
    return im;
  };
  let k = seed;
  const next = () => {
    k = (k * 1103515245 + 12345) & 0x7fffffff;
    return k / 0x7fffffff;
  };
  for (let z = shop ? 0 : 8; z + 7 < height; z += 9) {
    for (let a = 2; a + 3 < W; a += 5) p.decal(pane(next() < 0.35), "y", a, D, z + 7, true);
    for (let b = 2; b + 3 < D; b += 5) p.decal(pane(next() < 0.3), "x", W, b, z + 7, true);
  }
  // a door on the street side
  const door = new Bitmap(5, 8);
  door.rect(0, 0, 5, 8, [70, 56, 48]);
  door.rect(1, 1, 3, 6, [110, 88, 70]);
  p.decal(door, "y", Math.max(2, Math.floor(W / 2) - 2), D, 8);
  if (banner) {
    // a big red banner across the street side: what it is and what it costs
    const bw = Math.max(banner[0].length, banner[1].length) * 6 + 8;
    const ban = new Bitmap(bw, 19);
    ban.rect(0, 0, bw, 19, [232, 88, 72]);
    ban.strokeBox(0, 0, bw - 1, 18, [255, 236, 214]);
    drawText(ban, Math.floor((bw - banner[0].length * 6) / 2) + 1, 2, banner[0], [255, 250, 240]);
    drawText(ban, Math.floor((bw - banner[1].length * 6) / 2) + 1, 10, banner[1], [255, 226, 150]);
    p.decal(ban, "y", Math.max(1, Math.floor(W / 2 - bw / 4)), D, Math.min(height - 2, 26));
  }
  if (shop) {
    const COL: RGB[] = [[232, 88, 72], [34, 150, 140], [222, 164, 64], [90, 110, 190]];
    const awn = new Bitmap(Math.max(4, W * 2 - 4), 4);
    for (let x = 0; x < awn.w; x++) for (let y = 0; y < 4; y++) awn.set(x, y, (x >> 2) % 2 ? [246, 240, 228] : COL[seed % COL.length]);
    p.decal(awn, "y", 1, D, Math.min(height - 1, 10));
  }
  return { img: p.img, em: p.em, ax: p.ox, ay: p.oy, w: wTiles, h: dTiles } as unknown as IsoSprite;
}

/** A rooftop billboard: two posts and a red board, FOR SALE, the building's name and its price. */
function rooftopBillboard(name: string, price: string): Bitmap {
  const lines = ["FOR SALE", name.toUpperCase().slice(0, 16), price];
  const bw = Math.max(...lines.map((l) => l.length)) * 6 + 10;
  const bh = 30;
  const b = new Bitmap(bw, bh + 10);
  for (const x of [6, bw - 8]) b.rect(x, bh - 1, 2, 11, [60, 60, 70]);
  b.rect(0, 0, bw, bh, [232, 88, 72]);
  b.strokeBox(0, 0, bw - 1, bh - 1, [255, 236, 214]);
  b.rect(1, bh - 4, bw - 2, 3, [196, 66, 56]);
  lines.forEach((l, i) => drawText(b, Math.floor((bw - l.length * 6) / 2) + 1, 2 + i * 8, l, i === 2 ? [255, 226, 150] : [255, 250, 240]));
  return b.outlined();
}

/** An estate agent's board on two posts. */
function saleBoard(): IsoSprite {
  const p = spritePainter(1, 1, 16);
  p.box(1, T / 2, 0, 0.6, 0.6, 10, [120, 90, 64]);
  p.box(T - 1.6, T / 2, 0, 0.6, 0.6, 10, [120, 90, 64]);
  const b = new Bitmap(14, 8);
  b.rect(0, 0, 14, 8, [250, 246, 238]);
  b.rect(0, 0, 14, 3, [232, 88, 72]);
  b.rect(2, 5, 10, 1, [80, 80, 90]);
  p.decal(b, "y", 0.5, T / 2 + 0.7, 14);
  return { img: p.img, em: p.em, ax: p.ox, ay: p.oy, w: 1, h: 1 } as unknown as IsoSprite;
}

/** A street lamp: a dark pole and a warm lantern. */
function streetLamp(): IsoSprite {
  const p = spritePainter(1, 1, 24);
  p.box(T / 2 - 0.4, T / 2 - 0.4, 0, 0.8, 0.8, 18, [52, 56, 66]);
  p.box(T / 2 - 1.2, T / 2 - 1.2, 18, 2.4, 2.4, 2.4, [255, 220, 150], { top: [70, 70, 80] });
  return { img: p.img, em: p.em, ax: p.ox, ay: p.oy, w: 1, h: 1 } as unknown as IsoSprite;
}

/** The family car in the garage: long along y, parked nose-in. */
function familyCar(): { img: Bitmap; ax: number; ay: number } {
  const p = spritePainter(2, 4, 16);
  const c: RGB = [96, 140, 196];
  for (const y of [3, 17]) for (const x of [1, 10]) p.box(x, y, 0, 1.2, 3, 2.4, [34, 34, 40]);
  p.box(1.5, 1, 1.4, 9, 21, 4, c, { top: shade(c, 1.08) });
  p.box(2.5, 7, 5.4, 7, 9, 3.4, [160, 200, 228], { top: shade(c, 0.95) });
  p.box(3, 0.6, 2.8, 1.6, 0.4, 1, [250, 230, 160]);
  p.box(7.5, 0.6, 2.8, 1.6, 0.4, 1, [250, 230, 160]);
  return { img: p.img, ax: p.ox, ay: p.oy };
}

/** A small car, long along x, for the street. */
export function carSprite(seed: number): { img: Bitmap; ax: number; ay: number } {
  const p = spritePainter(2, 1, 12);
  const COL: RGB[] = [[232, 88, 72], [34, 150, 140], [222, 164, 64], [90, 110, 190], [236, 232, 222], [70, 74, 84]];
  const c = COL[seed % COL.length];
  for (const x of [1.5, 8.5]) for (const y of [0.4, 4.6]) p.box(x, y, 0, 2, 1, 2, [34, 34, 40]);
  p.box(0.5, 0.8, 1.2, 11, 4.4, 3.4, c, { top: shade(c, 1.08) });
  p.box(3, 1.2, 4.6, 6, 3.6, 2.6, [150, 196, 226], { top: shade(c, 0.95) });
  return { img: p.img, ax: p.ox, ay: p.oy };
}

/** The print shop's delivery van: a cab and a tall box in the company's colour. */
export function vanSprite(color: RGB): { img: Bitmap; ax: number; ay: number } {
  const p = spritePainter(3, 1, 16);
  for (const x of [1.5, 10.5]) for (const y of [0.4, 4.6]) p.box(x, y, 0, 2, 1, 2, [34, 34, 40]);
  // it drives towards -x, so the cab leads (drawn first: it's further from the viewer)
  p.box(0.5, 0.8, 1.2, 4, 4.4, 3.6, color, { top: shade(color, 1.08) });
  p.box(1, 1.2, 4.8, 3, 3.6, 2.2, [150, 196, 226], { top: shade(color, 0.95) });
  // the cargo box with a stripe in the company colour
  p.box(4.5, 0.6, 1.2, 9, 4.8, 7, [236, 232, 222], { top: [246, 242, 234] });
  p.box(4.5, 5.3, 3.4, 9, 0.2, 1.6, color);
  return { img: p.img, ax: p.ox, ay: p.oy };
}

// ------------------------------------------------------------------ street cars by era

/** A side profile: (s along the car from the tail, z up), convex, in order round the outline. */
type CarProfile = [number, number][];

interface CarStyle {
  L: number;
  body: CarProfile;
  cabin: CarProfile;
  /** where each wheel pair starts along s (wheels are 2 units long) */
  wheels: [number, number];
  bumper: "chrome" | "black" | "body";
  /** a thin light bar across the nose and tail instead of round lamps */
  bar?: boolean;
  /** the roof is all glass */
  glassRoof?: boolean;
  /** a chrome strip along the side */
  trim?: boolean;
}

const CAR_STYLES: Record<string, CarStyle> = {
  // rounded saloon of the late 60s and 70s: long bonnet, a domed cabin, chrome bumpers
  saloon: {
    L: 11.4, wheels: [1.6, 8.4], bumper: "chrome", trim: true,
    body: [[0.2, 1.3], [11.2, 1.3], [11.4, 2], [11.2, 3.4], [10.2, 3.9], [1, 3.9], [0.1, 3.4], [0, 2]],
    cabin: [[2.6, 3.9], [8.2, 3.9], [7.3, 6.1], [6.5, 6.6], [4, 6.6], [3.3, 6.1]],
  },
  // a small round runabout
  bubble: {
    L: 8.4, wheels: [1, 5.4], bumper: "chrome",
    body: [[0.3, 1.3], [8.1, 1.3], [8.4, 2.2], [8, 3.6], [7, 4.2], [1.2, 4.2], [0.2, 3.6], [0, 2.2]],
    cabin: [[1.4, 4.2], [6.6, 4.2], [5.7, 6.3], [4.6, 7], [2.6, 7], [1.8, 6.3]],
  },
  // the 80s: straight lines, a wedge nose, black bumpers
  wedge: {
    L: 11.6, wheels: [1.6, 8.6], bumper: "black",
    body: [[0, 1.2], [11.6, 1.2], [11.6, 3.3], [11.1, 3.75], [0, 4.1]],
    cabin: [[2.2, 4.05], [8, 3.85], [7.1, 6.6], [2.5, 6.6]],
  },
  estate: {
    L: 11.6, wheels: [1.6, 8.6], bumper: "black", trim: true,
    body: [[0, 1.2], [11.6, 1.2], [11.6, 3.3], [11.1, 3.75], [0, 4.1]],
    cabin: [[0.3, 4.1], [8, 3.85], [7.1, 6.6], [0.4, 6.6]],
  },
  // 90s and 2000s hatchback: short, a raked windscreen, an upright tailgate
  hatch: {
    L: 9.8, wheels: [1.2, 7], bumper: "body",
    body: [[0.1, 1.3], [9.4, 1.3], [9.8, 2], [9.6, 3.3], [7.6, 3.9], [0.2, 4.2], [0, 3.6]],
    cabin: [[0.3, 4.2], [7.4, 3.95], [5.2, 6.6], [1, 6.8], [0.3, 6]],
  },
  // a rounded family saloon of the 2000s
  family: {
    L: 10.8, wheels: [1.4, 8], bumper: "body",
    body: [[0.2, 1.3], [10.5, 1.3], [10.8, 2], [10.6, 3.4], [8.8, 4], [1.2, 4.2], [0.1, 3.6], [0, 2]],
    cabin: [[1.6, 4.2], [8.6, 4], [6.4, 6.7], [3, 6.8], [1.6, 5.8]],
  },
  // tiny city two-seater
  city: {
    L: 7, wheels: [0.8, 4.4], bumper: "body", bar: true,
    body: [[0.2, 1.3], [6.7, 1.3], [7, 2], [6.8, 3.6], [5.8, 4.4], [0.2, 4.6], [0, 3.8], [0, 2]],
    cabin: [[0.2, 4.6], [5.6, 4.4], [4, 7.2], [1, 7.4], [0.2, 6.8]],
  },
  // smooth electric car: low nose, no grille, a glass roof and light bars
  electric: {
    L: 10.4, wheels: [1.3, 7.7], bumper: "body", bar: true, glassRoof: true,
    body: [[0.2, 1.2], [10.1, 1.2], [10.4, 1.8], [10.3, 3], [8.4, 3.9], [0.4, 4.3], [0, 3.6], [0, 1.8]],
    cabin: [[0.6, 4.3], [8.2, 3.95], [5.4, 6.8], [2, 6.9], [0.6, 5.9]],
  },
};

/** Which shapes and paints are on the road in a given year (cars linger a few years after they go out of fashion). */
function carEra(year: number): { styles: string[]; paints: RGB[]; glass: RGB } {
  if (year < 1972) return { styles: ["saloon", "saloon", "bubble"], paints: [[150, 206, 180], [236, 224, 190], [140, 180, 214], [226, 120, 100], [214, 170, 70], [196, 70, 64]], glass: [150, 196, 226] };
  if (year < 1980) return { styles: ["saloon", "saloon", "bubble", "wedge"], paints: [[214, 120, 52], [140, 96, 64], [130, 140, 80], [226, 190, 80], [120, 150, 110], [180, 70, 60]], glass: [150, 196, 226] };
  if (year < 1990) return { styles: ["wedge", "wedge", "estate"], paints: [[200, 56, 52], [232, 230, 224], [176, 180, 186], [52, 64, 110], [204, 186, 150], [64, 66, 72]], glass: [146, 186, 214] };
  if (year < 2000) return { styles: ["hatch", "hatch", "family", "estate"], paints: [[40, 140, 140], [54, 110, 72], [110, 72, 140], [128, 40, 52], [70, 110, 170], [222, 200, 80]], glass: [120, 160, 196] };
  if (year < 2012) return { styles: ["hatch", "family", "family"], paints: [[186, 190, 196], [40, 60, 110], [52, 54, 60], [230, 196, 60], [168, 40, 44], [120, 160, 200]], glass: [104, 140, 176] };
  if (year < 2022) return { styles: ["family", "city", "electric", "hatch"], paints: [[238, 238, 236], [120, 126, 134], [80, 130, 190], [196, 50, 56], [52, 54, 62]], glass: [84, 110, 140] };
  return { styles: ["electric", "electric", "city"], paints: [[238, 238, 236], [120, 126, 134], [80, 130, 190], [196, 50, 56], [120, 200, 180], [60, 62, 70]], glass: [70, 92, 120] };
}

/**
 * A street car in the style of the year it was bought: rounded saloons in the 60s and 70s, boxy wedges in the 80s,
 * hatchbacks through the 90s and 2000s, small electric cars after. Long along x; `nose` is the way it drives.
 */
export function streetCar(year: number, seed: number, nose: 1 | -1): { img: Bitmap; ax: number; ay: number } {
  const r = artRng(seed * 7919 + year * 31 + 5);
  // older cars stay on the road a while
  const built = year - Math.floor(r() * r() * 14);
  const era = carEra(built);
  const st = CAR_STYLES[era.styles[Math.floor(r() * era.styles.length)]] ?? CAR_STYLES.hatch;
  const paint = era.paints[Math.floor(r() * era.paints.length)];
  const roof: RGB = st === CAR_STYLES.saloon && r() < 0.5 ? [240, 232, 210] : paint;
  const glass = era.glass;
  const p = spritePainter(2, 1, 12);
  const off = (12 - st.L) / 2;
  const X = (s: number) => off + (nose > 0 ? s : st.L - s);
  const by0 = 0.8;
  const by1 = 5.2;
  const lit = (c: RGB, nz: number) => shade(c, 0.74 + 0.44 * Math.max(0, nz));
  // wheels first: the body covers their tops
  for (const ws of st.wheels) {
    const x0 = Math.min(X(ws), X(ws + 2));
    for (const y of [0.4, 4.6]) p.box(x0, y, 0, 2, 1, 2, [34, 34, 40], { outline: false });
    p.box(x0 + 0.6, 5.55, 0.6, 0.8, 0.1, 0.8, [150, 150, 156], { outline: false });
  }
  // bumpers: the one at the far end (-x) is drawn before the body so the body hides it
  const bump: RGB = st.bumper === "chrome" ? [214, 218, 222] : st.bumper === "black" ? [50, 50, 56] : shade(paint, 0.82);
  const bx = (s: number) => Math.min(X(s), X(s + 0.6));
  const [farB, nearB] = nose > 0 ? [-0.2, st.L - 0.4] : [st.L - 0.4, -0.2];
  p.box(bx(farB), by0 - 0.1, 1.4, 0.6, by1 - by0 + 0.2, 0.8, bump, { outline: false });
  /** a convex profile pushed out across the car: the faces the camera can see, and the near side */
  const solid = (prof: CarProfile, y0: number, y1: number, side: RGB, face: (nz: number, i: number) => RGB) => {
    const pts = prof.map(([s, z]) => [X(s), z] as [number, number]);
    const cx = pts.reduce((a, q) => a + q[0], 0) / pts.length;
    const cz = pts.reduce((a, q) => a + q[1], 0) / pts.length;
    pts.forEach((a, i) => {
      const b = pts[(i + 1) % pts.length];
      let nx = b[1] - a[1];
      let nz = a[0] - b[0];
      if (nx * ((a[0] + b[0]) / 2 - cx) + nz * ((a[1] + b[1]) / 2 - cz) < 0) {
        nx = -nx;
        nz = -nz;
      }
      const len = Math.hypot(nx, nz) || 1;
      nx /= len;
      nz /= len;
      // seen from the camera's side (the view runs along (1, 1, 2))
      if (nx + 2 * nz <= 0.05) return;
      p.quad([[a[0], y0, a[1]], [b[0], y0, b[1]], [b[0], y1, b[1]], [a[0], y1, a[1]]], face(nz, i));
    });
    p.quad(pts.map(([x, z]) => [x, y1, z] as [number, number, number]), side);
    return { pts, cx, cz };
  };
  solid(st.body, by0, by1, paint, (nz) => lit(paint, nz));
  // the cabin: roof in the paint (or glass), the sloping screens in glass
  const cab = solid(st.cabin, by0 + 0.4, by1 - 0.4, paint, (nz) => (nz > 0.9 && !st.glassRoof ? lit(roof, nz) : lit(glass, nz * 0.6)));
  // side windows: the cabin side shrunk towards its middle, with a pillar
  const win = cab.pts.map(([x, z]) => [cab.cx + (x - cab.cx) * 0.8, cab.cz + (z - cab.cz) * 0.72 + 0.25] as [number, number]);
  p.quad(win.map(([x, z]) => [x, by1 - 0.39, z] as [number, number, number]), glass);
  const zLo = Math.min(...win.map((q) => q[1]));
  const zHi = Math.max(...win.map((q) => q[1]));
  p.line([cab.cx, by1 - 0.38, zLo], [cab.cx, by1 - 0.38, zHi], paint);
  if (st.trim) p.line([X(0.4), by1 + 0.01, 2.6], [X(st.L - 0.4), by1 + 0.01, 2.6], [214, 218, 222]);
  // the bumper on the camera's end (the far one went in before the body)
  p.box(bx(nearB), by0 - 0.1, 1.4, 0.6, by1 - by0 + 0.2, 0.8, bump, { outline: false });
  // lamps on the end facing the camera: headlamps if it's driving that way, red tail lamps if not
  const xEnd = Math.max(...st.body.map(([s]) => X(s))) + 0.02;
  const lamp: RGB = nose > 0 ? [252, 240, 180] : [214, 52, 52];
  if (st.bar) p.panelX(xEnd, by0 + 0.4, 2.7, by1 - by0 - 0.8, 0.5, nose > 0 ? [190, 240, 255] : [220, 60, 70]);
  else for (const y of [by0 + 0.5, by1 - 1.5]) p.panelX(xEnd, y, 2.4, 1, 0.8, lamp);
  return { img: p.img.outlined(OUTLINE), ax: p.ox + 1, ay: p.oy + 1 };
}

const CANOPY: Record<string, RGB[]> = {
  spring: [[70, 140, 78], [96, 158, 84], [214, 150, 176]],
  summer: [[52, 120, 70], [62, 132, 80], [44, 108, 64]],
  autumn: [[196, 116, 52], [214, 152, 64], [168, 84, 48]],
  winter: [[226, 232, 238], [206, 214, 222], [120, 104, 92]],
};

/** A garden tree straight in the ground: trunk, then a big layered canopy in the season's colours. */
function outdoorTree(seed: number, season: string): IsoSprite {
  if (season === "winter") return winterTree(seed);
  const p = spritePainter(1, 1, 40);
  const pal = CANOPY[season] ?? CANOPY.summer;
  p.box(T / 2 - 0.7, T / 2 - 0.7, 0, 1.4, 1.4, 8, [110, 74, 48], { top: [130, 90, 60] });
  const big = leafy(7 + (seed % 3), seed, pal[seed % 2]);
  const [sx, sy] = p.P(T / 2, T / 2, 8);
  p.img.blit(big, sx - (big.w >> 1), sy - big.h + 9);
  const top = leafy(5, seed + 5, pal[(seed + 1) % 2]);
  p.img.blit(top, sx - (top.w >> 1) + ((seed % 3) - 1), sy - big.h - top.h + 16);
  return { img: p.img, em: p.em, ax: p.ox, ay: p.oy, w: 1, h: 1 } as unknown as IsoSprite;
}

/** Winter: one tree in three is a snowy evergreen, the rest are bare branches with snow along the tops. */
function winterTree(seed: number): IsoSprite {
  const p = spritePainter(1, 1, 40);
  const [sx, sy] = p.P(T / 2, T / 2, 0);
  const snow: RGB = [240, 244, 250];
  const im = new Bitmap(28, 44);
  const cx = 14;
  const base = 43;
  if (seed % 3 === 0) {
    // evergreen: three dark tiers, each with a snowy upper edge
    const green: RGB = [36, 78, 62];
    im.rect(cx - 1, base - 7, 3, 7, [92, 64, 44]);
    for (let t = 0; t < 3; t++) {
      const yb = base - 6 - t * 10;
      const half = 11 - t * 3;
      im.poly([[cx - half, yb], [cx + half, yb], [cx, yb - 15]], shade(green, 0.9 + t * 0.08));
      for (let x = -half + 2; x <= half - 2; x++) {
        const yy = Math.round(yb - 15 + ((15 * Math.abs(x)) / half));
        im.set(cx + x, yy, snow);
        if (Math.abs(x) < half - 4) im.set(cx + x, yy + 1, snow);
      }
    }
  } else {
    // bare: a trunk that forks into branches, a line of snow sitting on each
    const bark: RGB = [84, 62, 50];
    const rnd = artRng(seed * 131 + 7);
    im.rect(cx - 1, base - 16, 3, 16, bark);
    const branch = (x0: number, y0: number, len: number, ang: number, depth: number) => {
      const x1 = x0 + Math.cos(ang) * len;
      const y1 = y0 - Math.sin(ang) * len;
      im.line(Math.round(x0), Math.round(y0), Math.round(x1), Math.round(y1), shade(bark, 1 - depth * 0.08));
      im.set(Math.round(x1), Math.round(y1) - 1, snow);
      im.set(Math.round((x0 + x1) / 2), Math.round((y0 + y1) / 2) - 1, snow);
      if (depth < 3) {
        branch(x1, y1, len * 0.7, ang + 0.35 + rnd() * 0.3, depth + 1);
        branch(x1, y1, len * 0.7, ang - 0.35 - rnd() * 0.3, depth + 1);
      }
    };
    branch(cx, base - 15, 9, Math.PI / 2 + (rnd() - 0.5) * 0.3, 0);
  }
  const out = im.outlined(OUTLINE);
  p.img.blit(out, sx - (out.w >> 1), sy - out.h + 3);
  return { img: p.img, em: p.em, ax: p.ox, ay: p.oy, w: 1, h: 1 } as unknown as IsoSprite;
}

/** A low hedge bush. */
function outdoorBush(seed: number, season: string): IsoSprite {
  const p = spritePainter(1, 1, 12);
  const pal = CANOPY[season] ?? CANOPY.summer;
  const b = leafy(6, seed, season === "winter" ? [96, 122, 92] : pal[seed % 3 === 2 ? 0 : seed % 2]);
  const [sx, sy] = p.P(T / 2, T / 2, 0);
  p.img.blit(b, sx - (b.w >> 1), sy - b.h + 3);
  return { img: p.img, em: p.em, ax: p.ox, ay: p.oy, w: 1, h: 1 } as unknown as IsoSprite;
}

function hashId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h % 1000;
}

/** Flattens a scene (plus extra sprites, e.g. people) into one bitmap: for tests, QA and thumbnails. */
export function flatten(scene: StudioScene, extra: SceneSprite[] = []): Bitmap {
  const out = new Bitmap(scene.w, scene.h);
  out.rect(0, 0, scene.w, scene.h, [30, 44, 56]);
  out.blit(scene.base, 0, 0);
  for (const s of [...scene.sprites, ...extra].sort((a, b) => a.depth - b.depth)) out.blit(s.img, s.px, s.py);
  return out;
}

/** A room-type thumbnail for the build tray (from hd2d_iso2.py). */
export function roomThumb(type: string, locked = false): Bitmap {
  const t = C.room[type];
  const wall = hex(t?.palette.wall ?? "#C8BEB0");
  const floor = hex(t?.palette.floor ?? "#96928A");
  const im = new Bitmap(40, 30);
  im.poly([[20, 12], [38, 21], [20, 29], [2, 21]], floor);
  im.poly([[2, 21], [2, 7], [20, -2], [20, 12]], wall);
  im.poly([[20, 12], [20, -2], [38, 7], [38, 21]], shade(wall, 0.82));
  im.line(2, 21, 20, 12, shade(wall, 0.7));
  im.line(20, 12, 38, 21, shade(wall, 0.7));
  const prop = t?.furniture[0];
  if (prop) {
    const s = furnitureSprite(prop, 0, 1);
    const k = Math.min(1, 18 / s.img.w);
    const small = k < 1 ? downscale(s.img, k) : s.img;
    im.blit(small, 20 - (small.w >> 1), 22 - small.h);
  }
  if (locked) for (let i = 0; i < im.data.length; i += 4) {
    const g = (im.data[i] + im.data[i + 1] + im.data[i + 2]) / 3 * 0.9 + 30;
    im.data[i] = im.data[i + 1] = im.data[i + 2] = g;
  }
  return im;
}

function downscale(b: Bitmap, k: number): Bitmap {
  const w = Math.max(1, Math.round(b.w * k));
  const h = Math.max(1, Math.round(b.h * k));
  const out = new Bitmap(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const c = b.get(Math.min(b.w - 1, Math.floor(x / k)), Math.min(b.h - 1, Math.floor(y / k)));
    if (c[3]) out.set(x, y, [c[0], c[1], c[2]], c[3]);
  }
  return out;
}

export { OUTLINE };
